import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Star,
  Navigation,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { CONTACT, SOCIAL_LINKS, MAPS } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goBook = () => {
    navigate("/book");
    window.scrollTo({ top: 0 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-[#0C0C0C] py-24 md:py-36 px-6 lg:px-10 overflow-hidden"
      data-testid="contact-section"
    >
      <div className="pointer-events-none absolute -top-40 left-0 h-[500px] w-[500px] rounded-full bg-teal/10 blur-[120px]" />

      <div className="relative mx-auto max-w-[1400px]">
        {/* Header + prominent CTA above contact info */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <Reveal>
              <p className="font-body text-xs md:text-sm uppercase tracking-[0.3em] text-teal mb-6">
                / Contact &amp; Visit
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-700 uppercase leading-[0.9] tracking-tighter text-white text-5xl md:text-6xl lg:text-7xl">
                Come train with us
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <button
              onClick={goBook}
              data-testid="contact-book-cta"
              className="font-display uppercase tracking-wide text-base px-8 py-4 bg-hotpink text-white hover:bg-white hover:text-ink transition-colors duration-300 rounded-sm hover:drop-shadow-[0_0_20px_rgba(255,20,147,0.5)]"
            >
              Book Your Free Class
            </button>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Business information */}
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-[#121212] p-7 md:p-9 flex flex-col">
              <h3 className="font-display uppercase tracking-tight text-2xl text-white">
                {CONTACT.business}
              </h3>

              <div className="mt-8 space-y-6">
                <a
                  href={MAPS.directionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-address-link"
                  className="group flex items-start gap-4 text-neutral-300 hover:text-white transition-colors"
                >
                  <MapPin size={20} className="text-hotpink mt-0.5 shrink-0" />
                  <span className="font-body text-base leading-relaxed">
                    {CONTACT.addressLines[0]}
                    <br />
                    {CONTACT.addressLines[1]}
                  </span>
                </a>

                <a
                  href={`tel:${CONTACT.phoneHref}`}
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 text-neutral-300 hover:text-white transition-colors"
                >
                  <Phone size={20} className="text-hotpink shrink-0" />
                  <span className="font-body text-base">{CONTACT.phone}</span>
                </a>

                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 text-neutral-300 hover:text-white transition-colors break-all"
                >
                  <Mail size={20} className="text-hotpink shrink-0" />
                  <span className="font-body text-base">{CONTACT.email}</span>
                </a>

                <a
                  href={SOCIAL_LINKS.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-instagram-link"
                  className="flex items-center gap-4 text-neutral-300 hover:text-white transition-colors"
                >
                  <Instagram size={20} className="text-hotpink shrink-0" />
                  <span className="font-body text-base">{SOCIAL_LINKS.instagramHandle}</span>
                </a>

                <a
                  href={SOCIAL_LINKS.facebookUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-facebook-link"
                  className="flex items-center gap-4 text-neutral-300 hover:text-white transition-colors"
                >
                  <Facebook size={20} className="text-hotpink shrink-0" />
                  <span className="font-body text-base">War Room Performance</span>
                </a>
              </div>

              {/* Action buttons */}
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={SOCIAL_LINKS.facebookReviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-review-btn"
                  className="inline-flex items-center gap-2 font-display uppercase tracking-wide text-sm px-6 py-3 border border-white/20 text-white hover:border-teal hover:text-teal transition-colors rounded-sm"
                >
                  <Star size={16} /> Leave Us a Review
                </a>
                <a
                  href={MAPS.directionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-directions-btn"
                  className="inline-flex items-center gap-2 font-display uppercase tracking-wide text-sm px-6 py-3 border border-white/20 text-white hover:border-teal hover:text-teal transition-colors rounded-sm"
                >
                  <Navigation size={16} /> Get Directions
                </a>
              </div>

              {/* Google Maps embed */}
              <div className="mt-8 overflow-hidden rounded-lg border border-white/10 grow min-h-[240px]">
                <iframe
                  title="War Room Performance location"
                  src={MAPS.embedUrl}
                  className="w-full h-full min-h-[240px]"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map-embed"
                />
              </div>
            </div>
          </Reveal>

          {/* Contact form */}
          <Reveal delay={0.15}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-7 md:p-9">
              {done ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-16 text-center"
                  data-testid="contact-success"
                >
                  <CheckCircle2 size={56} className="text-teal mx-auto" />
                  <h3 className="mt-6 font-display uppercase tracking-tight text-3xl text-white">
                    Message sent
                  </h3>
                  <p className="mt-3 font-body text-neutral-300">
                    Thanks for reaching out — we&apos;ll get back to you soon.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="contact-reset-btn"
                    className="mt-8 font-display uppercase tracking-wide text-sm px-6 py-3 border border-white/20 text-white hover:border-teal hover:text-teal transition-colors rounded-sm"
                  >
                    Send another
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                  <h3 className="font-display uppercase tracking-tight text-2xl text-white">
                    Send us a message
                  </h3>

                  <Field
                    label="Name"
                    value={form.name}
                    onChange={(v) => update("name", v)}
                    testid="contact-name"
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(v) => update("email", v)}
                    testid="contact-email"
                  />
                  <Field
                    label="Phone number"
                    value={form.phone}
                    onChange={(v) => update("phone", v)}
                    testid="contact-phone"
                  />
                  <div>
                    <label className="block font-body text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">
                      Message
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      rows={5}
                      data-testid="contact-message"
                      className="w-full px-4 py-3 bg-[#1A1A1A] border border-white/15 rounded-sm text-white font-body text-sm placeholder:text-neutral-600 focus:border-hotpink focus:outline-none transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="w-full font-display uppercase tracking-wide text-base px-8 py-4 bg-hotpink text-white hover:bg-white hover:text-ink transition-colors duration-300 rounded-sm disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 size={18} className="animate-spin" />}
                    {loading ? "Sending..." : "Submit"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block font-body text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-12 px-4 bg-[#1A1A1A] border border-white/15 rounded-sm text-white font-body text-sm placeholder:text-neutral-600 focus:border-hotpink focus:outline-none transition-colors"
      />
    </div>
  );
}
